import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Instagram, Images, Maximize2, PlayCircle, X } from "lucide-react";
import InstagramEmbed from "../../components/instagram/InstagramEmbed";
import { instagramPostLinks } from "../../data/instagramPostLinks";
import "./InstagramUpdatesPage.css";

const PROFILE = "https://www.instagram.com/symmachinery/";
const PAGE_SIZE = 6;
const validPost = (url) => /^https:\/\/(www\.)?instagram\.com\/(p|reel|tv)\/[A-Za-z0-9_-]+\/?(?:\?.*)?$/.test(url || "");

function InstagramPostViewer({ post, onClose }) {
  useEffect(() => {
    if (!post) return undefined;
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = oldOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [post, onClose]);

  if (!post) return null;
  return (
    <div className="sym-ig-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="sym-ig-modal" role="dialog" aria-modal="true" aria-label={post.title || "Instagram post"}>
        <header className="sym-ig-modal-header">
          <span><Instagram size={18} /> {post.title || "SYM Machinery update"}</span>
          <button type="button" onClick={onClose} aria-label="Close post viewer"><X size={22} /></button>
        </header>
        <div className="sym-ig-modal-content"><InstagramEmbed permalink={post.url} label="SYM Machinery post" /></div>
        <p className="sym-ig-modal-note">The post opens here. Instagram controls any links inside its own embed.</p>
      </section>
    </div>
  );
}

export default function InstagramUpdatesPage() {
  const [shown, setShown] = useState(PAGE_SIZE);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const viewerRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(document.fullscreenElement === viewerRef.current);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === viewerRef.current) {
        await document.exitFullscreen();
      } else if (viewerRef.current?.requestFullscreen) {
        await viewerRef.current.requestFullscreen();
      }
    } catch (error) {
      console.warn("Fullscreen preview is not available in this browser", error);
    }
  };

  const posts = useMemo(() => instagramPostLinks.filter((post) => validPost(post.url)), []);
  const visiblePosts = posts.slice(0, shown);
  const moreAvailable = shown < posts.length;

  return (
    <div className="sym-ig-page">
      <Helmet>
        <title>Latest SYM Machinery Updates | Instagram Posts & Reels</title>
        <meta name="description" content="See SYM Machinery's latest Instagram updates, public posts, reels and forklift demonstrations in one place." />
        <link rel="canonical" href="https://symforklifts.com/instagram-updates" />
      </Helmet>

      <section className="sym-ig-page-header">
        <div className="sym-ig-wide">
          <span className="sym-ig-eyebrow"><Instagram size={18} /> FOLLOW SYM MACHINERY</span>
          <h1>Latest <span>Updates & Reels</span></h1>
          <p>Forklifts in action, new arrivals, machinery deliveries and behind-the-scenes stories from the SYM Machinery team.</p>
          <div className="sym-ig-header-actions">
            <a className="sym-ig-profile-link" href={PROFILE} target="_blank" rel="noopener noreferrer"><Instagram size={18} /> @symmachinery <ExternalLink size={15} /></a>
            <Link to="/machines" className="sym-ig-machines-link">Explore Machines <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      <section className="sym-ig-full-bleed" aria-labelledby="sym-ig-profile-title">
        <div className="sym-ig-wide">
          <div className="sym-ig-section-heading">
            <div>
              <span className="sym-ig-overline">OFFICIAL INSTAGRAM PREVIEW</span>
              <h2 id="sym-ig-profile-title">@symmachinery</h2>
              <p>View SYM Machinery's public Instagram profile preview directly on our website.</p>
            </div>
            <button type="button" className="sym-ig-chip sym-ig-expand-button" onClick={toggleFullscreen}>
              <Maximize2 size={15} /> {isFullscreen ? "Exit fullscreen" : "Expand viewer"}
            </button>
          </div>
          <div className="sym-ig-profile-stage sym-ig-webview-style" ref={viewerRef}>
            <div className="sym-ig-webview-bar">
              <span className="sym-ig-webview-brand"><Instagram size={18} /> SYM Machinery Updates</span>
              <span className="sym-ig-webview-address" title="Official Instagram preview">instagram.com/symmachinery</span>
              <a href={PROFILE} target="_blank" rel="noopener noreferrer" aria-label="Open official Instagram profile">
                <ExternalLink size={17} />
              </a>
            </div>
            <div className="sym-ig-webview-content">
              <aside className="sym-ig-viewer-context">
                <span className="sym-ig-overline">LATEST FROM OUR TEAM</span>
                <h3>Discover SYM Machinery on Instagram</h3>
                <p>Watch new arrivals, machine demonstrations and real-world updates from our team.</p>
                <p>New public posts appear in Instagram's official profile preview when Meta refreshes the embed.</p>
                <a href={PROFILE} target="_blank" rel="noopener noreferrer">
                  Open full Instagram profile <ExternalLink size={16}/>
                </a>
              </aside>
              <div className="sym-ig-sdk-feed">
                <InstagramEmbed permalink={PROFILE} label="@symmachinery profile" />
              </div>
            </div>
            <div className="sym-ig-webview-footer">
              This is Instagram's official public profile preview, not a full Instagram browser.
            </div>
          </div>
          <p className="sym-ig-legal-note">Instagram's official profile preview has a limited number of posts and a fixed content width. Embedded profile clicks and navigation are controlled by Instagram, not this website.</p>
        </div>
      </section>

      {posts.length > 0 && (
        <section className="sym-ig-gallery-section sym-ig-wide" aria-labelledby="sym-ig-gallery-title">
          <div className="sym-ig-section-heading">
            <div>
              <span className="sym-ig-overline">POSTS & REELS</span>
              <h2 id="sym-ig-gallery-title">Explore more updates</h2>
              <p>Select a post to open its embed on this page.</p>
            </div>
            <span className="sym-ig-chip"><Images size={15} /> {posts.length} updates</span>
          </div>
          <div className="sym-ig-post-grid">
            {visiblePosts.map((post, index) => {
              const reel = /\/(reel|tv)\//.test(post.url);
              return (
                <button className="sym-ig-post-card" type="button" key={post.url} onClick={() => setSelectedPost(post)} aria-label={`Open ${post.title || `post ${index+1}`} on this page`}>
                  <div className="sym-ig-post-poster">
                    {post.thumbnail ? <img src={post.thumbnail} loading="lazy" alt={post.title || "SYM Machinery post"} /> : <Instagram size={52} aria-hidden="true" />}
                    <span className="sym-ig-poster-type">{reel ? <PlayCircle size={15} /> : <Images size={15} />}{reel ? "Reel" : "Post"}</span>
                  </div>
                  <div className="sym-ig-post-meta"><strong>{post.title || `SYM Machinery update ${index+1}`}</strong><span>View on this page <ArrowRight size={16} /></span></div>
                </button>
              );
            })}
          </div>
          {moreAvailable && (
            <button type="button" className="sym-ig-load-more" onClick={() => setShown((count) => Math.min(count + PAGE_SIZE, posts.length))}>
              Load More Updates <ArrowRight size={18} />
            </button>
          )}
        </section>
      )}

      <section className="sym-ig-end-cta sym-ig-wide">
        <div><h2>More from SYM Machinery</h2><p>Follow our latest machinery arrivals and demonstrations. Or explore our current machines.</p></div>
        <Link to="/machines">Browse Machines <ArrowRight size={18} /></Link>
      </section>
      {selectedPost && <InstagramPostViewer post={selectedPost} onClose={() => setSelectedPost(null)} />}
    </div>
  );
}
